import { create } from "zustand";
import { devtools } from "zustand/middleware";

interface CustomersUIState {
  // View state
  searchTerm: string;
  debouncedSearchTerm: string;

  // Selection state
  selectedCustomerIds: string[];
  selectedCustomerId: string | null;

  // Filter state - null means "no filters applied"
  activeTierFilters: Set<string>;

  // Dialog state
  showManagementDialog: boolean;
  showDetailDialog: boolean;
  editingCustomerId: string | null;

  // UI state
  isScrolled: boolean;
  showMobileSearch: boolean;

  // Actions
  setSearchTerm: (term: string) => void;
  setDebouncedSearchTerm: (term: string) => void;
  setSelectedCustomerIds: (customerIds: string[]) => void;
  clearSelectedCustomerIds: () => void;
  toggleCustomerSelection: (customerId: string) => void;
  toggleTierFilter: (tierId: string) => void;
  setShowManagementDialog: (show: boolean) => void;
  setShowDetailDialog: (show: boolean) => void;
  setEditingCustomerId: (customerId: string | null) => void;
  setSelectedCustomerId: (customerId: string | null) => void;
  setIsScrolled: (scrolled: boolean) => void;
  setShowMobileSearch: (show: boolean) => void;
  toggleMobileSearch: () => void;
  clearAllFilters: () => void;
}

export const useCustomersUIStore = create<CustomersUIState>()(
  devtools(
    (set) => ({
      // Initial state
      searchTerm: "",
      debouncedSearchTerm: "",
      selectedCustomerIds: [],
      selectedCustomerId: null,
      activeTierFilters: new Set(),
      showManagementDialog: false,
      showDetailDialog: false,
      editingCustomerId: null,
      isScrolled: false,
      showMobileSearch: false,

      // Actions
      setSearchTerm: (searchTerm) => set({ searchTerm }),
      setDebouncedSearchTerm: (debouncedSearchTerm) => set({ debouncedSearchTerm }),

      setSelectedCustomerIds: (selectedCustomerIds) => set({ selectedCustomerIds }),
      clearSelectedCustomerIds: () => set({ selectedCustomerIds: [] }),
      toggleCustomerSelection: (customerId) =>
        set((state) => ({
          selectedCustomerIds: state.selectedCustomerIds.includes(customerId)
            ? state.selectedCustomerIds.filter((id) => id !== customerId)
            : [...state.selectedCustomerIds, customerId],
        })),

      toggleTierFilter: (tierId) =>
        set((state) => {
          const newActiveTierFilters = new Set(state.activeTierFilters);
          if (newActiveTierFilters.has(tierId)) {
            newActiveTierFilters.delete(tierId);
          } else {
            newActiveTierFilters.add(tierId);
          }
          return { activeTierFilters: newActiveTierFilters };
        }),

      setShowManagementDialog: (showManagementDialog) => set({ showManagementDialog }),
      setShowDetailDialog: (showDetailDialog) => set({ showDetailDialog }),
      setEditingCustomerId: (editingCustomerId) => set({ editingCustomerId }),
      setSelectedCustomerId: (selectedCustomerId) => set({ selectedCustomerId }),
      setIsScrolled: (isScrolled) => set({ isScrolled }),
      setShowMobileSearch: (showMobileSearch) => set({ showMobileSearch }),
      toggleMobileSearch: () =>
        set((state) => {
          const newShowMobileSearch = !state.showMobileSearch;
          return {
            showMobileSearch: newShowMobileSearch,
            // Clear search text when closing mobile search
            searchTerm: newShowMobileSearch ? state.searchTerm : "",
            debouncedSearchTerm: newShowMobileSearch ? state.debouncedSearchTerm : "",
          };
        }),

      clearAllFilters: () =>
        set({
          searchTerm: "",
          debouncedSearchTerm: "",
          selectedCustomerIds: [],
          activeTierFilters: new Set(),
        }),
    }),
    { name: "customers-ui-store" }
  )
);
